import { orderStatusBucket } from './orderStatus.ts';

/** Orders older than this never appear on the Unpaid Deposits page and never count in badges (PM, 2026-09-16). */
export const UNPAID_WINDOW_SECONDS = 72 * 3600;

export type ProofOrderLike = {
  asset?: string;
  status?: string;
  createdAt?: number;
  publicId?: string;
  kind?: 'deposit' | 'withdraw';
};

/**
 * Whether this order uses the manual-proof (22-character code / receipt) recovery
 * flow. Deliberately hard-coded to Argentine transfers (PM, 2026-09-16): there is no
 * per-channel switch, and a future market that needs recovery gets its own screens
 * rather than a flag on this one (doc/payments/AR_PAYMENT_PROOFS.md).
 */
export function proofSupported(order: ProofOrderLike): boolean {
  return String(order.asset || '').toUpperCase() === 'ARS';
}

const PLAYER_CANCELLED = new Set(['cancelled', 'canceled']);

export function isUnpaidDeposit(order: ProofOrderLike, nowSeconds: number): boolean {
  if (order.kind && order.kind !== 'deposit') return false;
  if (!proofSupported(order)) return false;
  const createdAt = Number(order.createdAt || 0);
  if (!createdAt || nowSeconds - createdAt > UNPAID_WINDOW_SECONDS) return false;
  const status = String(order.status || '').trim().toLowerCase();
  if (PLAYER_CANCELLED.has(status)) return false;
  return orderStatusBucket(status, 'deposit') !== 'completed';
}

export function unpaidDepositOrders<T extends ProofOrderLike>(orders: T[], nowSeconds: number): T[] {
  return orders
    .filter((order) => isUnpaidDeposit(order, nowSeconds))
    .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
}

export type ProofStatus = 'pending' | 'needs_info' | 'verified' | 'rejected';
export type UnpaidCardState = 'unpaid' | 'in_review' | 'resubmit' | 'credited';

/** Card state on the Unpaid Deposits page. `verified` still shows as in review: verified ≠ credited. */
export function unpaidCardState(orderStatus: string | undefined, latestProofStatus: ProofStatus | null | undefined): UnpaidCardState {
  if (orderStatusBucket(orderStatus, 'deposit') === 'completed') return 'credited';
  if (latestProofStatus === 'pending' || latestProofStatus === 'verified') return 'in_review';
  if (latestProofStatus === 'needs_info' || latestProofStatus === 'rejected') return 'resubmit';
  return 'unpaid';
}
